import moment from 'moment';
import { put, select, takeLatest } from 'redux-saga/effects';
import { AppTableInitialState, createAppTableSaga, createAppTableStore, createPersist } from 'components/AppTable/store';
import apiRoutes from 'config/apiRoute';
import { convertMinsToHrsMins } from 'utils/string.utils';
import { toTimeEntriesParams } from 'services/timeTracking/tableParams';
import { PayloadAction } from '@reduxjs/toolkit';

const startOfWeek = moment().startOf('week').format('YYYY-MM-DD');
const endOfWeek = moment().endOf('week').format('YYYY-MM-DD');

export const timelogReportSlice = createAppTableStore({
  name: 'timelogReport' as const,
  url: apiRoutes.TIME_ENTRIES,
  initialState: {
    params: {
      filters: {
        date_start: { value: startOfWeek },
        date_end: { value: endOfWeek },
        project_uuid: [],
        user_uuid: [],
        project_plan: [
          { value: 'starter' },
          { value: 'premium' },
          { value: 'enterprise' },
          { value: 'custom' },
          { value: 'hosting' },
        ],
        user_type: [{ value: 'regular_user' }, { value: 'freelancer' }],
        source: [{ value: 'task' }, { value: 'request' }],
        request_uuid: [],
      },
    },
    urlParams: { keyFilters: ['is_retainer', 'date_start', 'date_end', 'category'] },
    totalTime: null as null | string,
    apiParamsHandler: toTimeEntriesParams,
  },
  reducers: {
    setTotalTime: (state, { payload }: PayloadAction<string>) => {
      state.totalTime = payload;
    },
  },
});

function* handleApiResponseSagaListener({ payload }: { payload: any }) {
  yield put(timelogReportSlice.actions.setTotalTime(convertMinsToHrsMins(Number(payload?.meta?.total_minutes ?? 0))));
}

function* onRehydrate() {
  const store: AppTableInitialState = yield select((globalStore) => globalStore[timelogReportSlice.name] as AppTableInitialState);

  const hasPlan =
    store?.params?.filters &&
    store.params.filters.project_plan &&
    Array.isArray(store.params.filters.project_plan) &&
    store.params.filters.project_plan.length > 0;

  if (!hasPlan) {
    yield put(
      timelogReportSlice.actions.setFilter({
        project_plan: [
          { value: 'starter' },
          { value: 'premium' },
          { value: 'enterprise' },
          { value: 'custom' },
          { value: 'hosting' },
        ],
      }),
    );
  }

  // The "By category" filter is now single-select; normalize any persisted multi-select array.
  if (Array.isArray(store?.params?.filters?.category)) {
    yield put(timelogReportSlice.actions.setFilter({ category: undefined }));
  }
}

export const timelogReportSaga = createAppTableSaga(timelogReportSlice, function* customGenerator() {
  yield takeLatest(timelogReportSlice.actions.handleApiResponse, handleApiResponseSagaListener);
  yield takeLatest('persist/REHYDRATE', onRehydrate);
});

export const timelogReportReducer = createPersist(timelogReportSlice, []);
